import * as React from "react"
import { createContext, useContext, useState } from "react"

export type Language = "en" | "uk"

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: React.ReactNode
  initialLanguage?: Language
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  initialLanguage,
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Priority 1: Initial language from SSR/routing
    if (initialLanguage) {
      return initialLanguage
    }

    // Priority 2: During SSR, default to English
    if (typeof window === "undefined") {
      return "en"
    }

    // Priority 3: LocalStorage preference
    const stored = localStorage.getItem("preferred-language")
    if (stored === "en" || stored === "uk") {
      return stored
    }

    // Priority 4: Browser language
    const browserLang = navigator.language.toLowerCase()
    if (browserLang.startsWith("uk")) {
      return "uk"
    }
    if (browserLang.startsWith("en")) {
      return "en"
    }

    // Priority 5: Default to English
    return "en"
  })

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    if (typeof window !== "undefined") {
      localStorage.setItem("preferred-language", lang)
    }
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}
